import * as React from 'react';
import clsx from 'clsx';
import { ClickAwayListener, Tooltip as MuiTooltip, useControlled, useForkRef, } from '@mui/material';
import { omit } from 'lodash-es';
import getStyle, { tooltipClasses } from './lib/style';
import useDevelopmentWarning from '../hooks/useDevelopmentWarning';
import { Popover } from '../Popover';
export { tooltipClasses };
function composeEventHandler(handler, eventHandler) {
    return (event) => {
        const result = eventHandler?.(event);
        handler(event);
        return result;
    };
}
export function toArray(val) {
    if (!val)
        return [];
    return Array.isArray(val) ? val : [val];
}
export const Tooltip = React.forwardRef(({ arrow = false, title, children, describeChild, classes, mode, width, maxWidth, disablePortal = false, PopperProps = {}, trigger = ['hover', 'touch', 'focus'], open: openProp, onClose, onOpen, className, sx, 
// We extract the `enterDelay` prop and use 0 instead, to avoid the delay for ax
// Then we use CSS animation to emulate the delay.
enterDelay, disabled, ...restProps }, ref) => {
    const [openState, setOpenState] = useControlled({
        controlled: openProp,
        default: false,
        name: 'Tooltip',
        state: 'open',
    });
    const popupRef = React.useRef(null);
    const handlePopupRef = useForkRef(popupRef, PopperProps.ref);
    const delayRef = React.useRef();
    const childrenProps = children.props;
    const newChildrenProps = {};
    const showActions = new Set(toArray(trigger));
    const clearDelay = () => {
        clearTimeout(delayRef.current);
    };
    const internalTriggerOpen = (nextOpen) => {
        if (openState !== nextOpen) {
            setOpenState(nextOpen);
        }
    };
    const isInvalid = !!disabled || !title;
    const triggerOpen = (nextOpen, delay = 0, callback = () => { }) => {
        clearDelay();
        if (isInvalid && nextOpen) {
            return;
        }
        if (delay === 0) {
            internalTriggerOpen(nextOpen);
            callback();
        }
        else {
            delayRef.current = setTimeout(() => {
                internalTriggerOpen(nextOpen);
                callback();
            }, delay);
        }
    };
    React.useLayoutEffect(() => {
        if (isInvalid && openState) {
            triggerOpen(false, 0, () => onClose?.());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isInvalid]);
    const disabledListener = {
        disableFocusListener: !showActions.has('focus'),
        disableHoverListener: !showActions.has('hover'),
        disableTouchListener: !showActions.has('touch'),
    };
    if (!arrow) {
        // Handles the open / close events.
        // if arrow = true, we will use Popover instead. Popover will take care of these handlers, so we shouldn't handle them in that case.
        if (showActions.has('click')) {
            newChildrenProps.onClick = composeEventHandler((event) => {
                triggerOpen(true, 0, () => {
                    onOpen?.(event);
                });
            }, childrenProps.onClick);
        }
        if (showActions.has('contextMenu')) {
            newChildrenProps.onContextMenu =
                composeEventHandler((event) => {
                    event.preventDefault();
                    triggerOpen(true, 0, () => {
                        onOpen?.(event);
                    });
                }, childrenProps.onContextMenu);
        }
        newChildrenProps.onKeyDown = (event) => {
            if (event.key === 'Enter' || event.key === ' ') {
                triggerOpen(true, 0, () => {
                    onOpen?.(event);
                });
            }
            else if (event.key === 'Tab' && !disablePortal) {
                // AX: When there is no disablePortal state, the current child clicks the Tab button. If the Tooltip is in the open state, close the Tooltip.
                if (openState) {
                    triggerOpen(false, restProps?.leaveDelay ?? 0, () => {
                        onClose?.(event);
                    });
                }
            }
            childrenProps.onKeyDown?.(event);
        };
    }
    // Fix for Voice Over: cannot read dynamic aria-describedby when using VO+Arrow Keys
    // - For simple string title, use the alternative (static) aria-description
    // - For complex title content with click action, change the aria-expanded dynamically to force VO to read again
    if (!isInvalid && describeChild !== false) {
        if (typeof title === 'string') {
            newChildrenProps['aria-description'] =
                restProps['aria-description'] ?? title;
        }
        else if (!arrow &&
            (showActions.has('click') || showActions.has('contextMenu'))) {
            newChildrenProps['aria-expanded'] =
                restProps['aria-expanded'] ?? !!openState;
        }
    }
    useDevelopmentWarning(
    /* istanbul ignore next */
    () => showActions.has('click') || showActions.has('contextMenu'), "[Tooltip] You specified click or context menu trigger for Tooltip. It's probably not what you need for most cases. It's recommended to use Popover instead.");
    useDevelopmentWarning(
    /* istanbul ignore next */
    () => arrow, "[Tooltip] You specified an arrow for Tooltip. It's probably not what you need for most cases. It's recommended to use Popover instead.");
    if (arrow) {
        const actualTrigger = showActions.has('hover') || showActions.has('touch')
            ? 'hover'
            : 'click';
        return (React.createElement(Popover, { content: title, width: width, maxWidth: maxWidth, disablePortal: disablePortal, trigger: actualTrigger, open: openProp, onOpen: onOpen, onClose: onClose, className: className, placement: "top", sx: sx, mode: mode, disabled: disabled, enterDelay: enterDelay, role: "tooltip", ...restProps, ref: ref }, React.cloneElement(children, newChildrenProps)));
    }
    return (React.createElement(ClickAwayListener, { onClickAway: (e) => {
            const { target } = e;
            const inPopupOrChild = popupRef.current === target ||
                popupRef.current?.contains(target);
            if (!inPopupOrChild && openState) {
                triggerOpen(false, restProps?.leaveDelay ?? 0, () => onClose?.(e));
            }
        } },
        React.createElement(MuiTooltip, { className: className, sx: sx, classes: {
                ...classes,
                popper: clsx(tooltipClasses.popper, classes?.popper),
                tooltip: clsx(tooltipClasses.tooltip, classes?.tooltip),
            }, open: openState, placement: "top", arrow: false, describeChild: describeChild ?? true, PopperProps: {
                ...PopperProps,
                disablePortal: PopperProps.disablePortal ?? disablePortal,
                sx: [
                    (theme) => getStyle(theme, { mode, width, maxWidth }),
                    ...(Array.isArray(PopperProps.sx)
                        ? PopperProps.sx
                        : [PopperProps.sx]),
                ],
                ref: handlePopupRef,
            }, TransitionProps: openState
                ? {
                    style: {
                        transitionDelay: `${enterDelay ?? 100}ms`,
                        transitionDuration: 150,
                    },
                    timeout: (enterDelay ?? 100) + 150,
                }
                : {}, onOpen: (event) => {
                triggerOpen(true);
                onOpen?.(event);
            }, onClose: (event) => {
                triggerOpen(false);
                onClose?.(event);
            }, ref: ref, title: title, enterDelay: 0, ...omit(restProps, ['style', 'sx', 'className']), ...disabledListener }, React.cloneElement(children, newChildrenProps))));
});
